import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Building2,
    FileText,
    Hash,
    KeyRound,
    Loader2,
    Mail,
    Package,
    Pencil,
    Phone,
    Plus,
    Trash2,
    User,
    UserCircle2,
} from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/apiError";
import { Customer, CustomerProduct } from "../../types";
import { formatDate, formatMoney } from "../../utils/format";
import PageHeader from "../../components/ui/PageHeader";
import Card, { CardHeader, InfoTile } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { EmptyState } from "../../components/ui/DataTable";
import CustomerFormModal from "./CustomerFormModal";
import CustomerProductTable from "../customerProducts/CustomerProductTable";
import CustomerProductFormModal from "../customerProducts/CustomerProductFormModal";

export default function CustomerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { hasRole } = useAuth();
    const isAdmin = hasRole("SUPERADMIN");

    const customerReq = useApi<Customer>(`/customers/${id}`);
    const productsReq = useApi<CustomerProduct[]>("/customer-product", { customerId: id });
    const customer = customerReq.data;

    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [cpFormOpen, setCpFormOpen] = useState(false);
    const [editingCp, setEditingCp] = useState<CustomerProduct | null>(null);
    const [deletingCp, setDeletingCp] = useState<CustomerProduct | null>(null);

    const summary = useMemo(() => {
        const rows = productsReq.data ?? [];
        return {
            total: rows.reduce((s, r) => s + Number(r.amount), 0),
            hardware: rows.filter((r) => r.product.type === "HARDWARE").length,
            software: rows.filter((r) => r.product.type === "SOFTWARE").length,
            unpaid: rows.filter((r) => r.paymentStatus !== "PAID").length,
        };
    }, [productsReq.data]);

    if (customerReq.loading && !customer) {
        return (
            <div className="flex justify-center py-24">
                <Loader2 className="text-[#8b5cf6] animate-spin" size={36} />
            </div>
        );
    }

    if (!customer) {
        return (
            <Card>
                <EmptyState
                    title="Customer not found"
                    text={customerReq.error || "It may have been deleted, or you don't have access."}
                    action={
                        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/customers")}>
                            Back to customers
                        </Button>
                    }
                />
            </Card>
        );
    }

    const handleDelete = async () => {
        try {
            await api.delete(`/customers/${customer.id}`);
            toast.success("Customer deleted");
            navigate("/customers", { replace: true });
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleDeleteCp = async () => {
        if (!deletingCp) return;
        try {
            await api.delete(`/customer-product/${deletingCp.id}`);
            toast.success("Removed from customer");
            setDeletingCp(null);
            productsReq.reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const openAssign = () => {
        setEditingCp(null);
        setCpFormOpen(true);
    };

    return (
        <>
            <PageHeader
                back={
                    <Link to="/customers" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#a78bfa] mb-3 transition-colors">
                        <ArrowLeft size={14} />
                        Customers
                    </Link>
                }
                title={
                    <span className="flex flex-wrap items-center gap-3">
                        {customer.name}
                        <span className="font-mono text-base text-[#a78bfa]">#{customer.customerId}</span>
                    </span>
                }
                subtitle={customer.legalName}
                actions={
                    <>
                        {isAdmin && (
                            <>
                                <Button variant="secondary" icon={Pencil} onClick={() => setEditOpen(true)}>
                                    Edit
                                </Button>
                                <Button variant="danger" icon={Trash2} onClick={() => setDeleteOpen(true)}>
                                    Delete
                                </Button>
                            </>
                        )}
                        <Button icon={Plus} onClick={openAssign}>
                            Assign product
                        </Button>
                    </>
                }
            />

            <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
                {/* Details */}
                <Card className="2xl:col-span-1">
                    <CardHeader title="Details" icon={Building2} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-1 gap-3">
                        <InfoTile icon={<Hash size={18} />} label="CR Number" value={customer.crNo} />
                        <InfoTile
                            icon={<FileText size={18} />}
                            label="TRN"
                            value={customer.trnNotApplicable ? <Badge tone="slate">Not applicable</Badge> : customer.trn || "—"}
                        />
                        <InfoTile icon={<User size={18} />} label="Contact Person" value={customer.contactPerson || "—"} />
                        <InfoTile
                            icon={<Phone size={18} />}
                            label="Mobile"
                            value={
                                <>
                                    <a href={`tel:${customer.mobile1}`} className="hover:text-[#a78bfa]">
                                        {customer.mobile1}
                                    </a>
                                    {customer.mobile2 && (
                                        <a href={`tel:${customer.mobile2}`} className="block text-sm text-slate-400 hover:text-[#a78bfa]">
                                            {customer.mobile2}
                                        </a>
                                    )}
                                </>
                            }
                        />
                        <InfoTile
                            icon={<Mail size={18} />}
                            label="Email"
                            value={
                                customer.email ? (
                                    <a href={`mailto:${customer.email}`} className="hover:text-[#a78bfa]">
                                        {customer.email}
                                    </a>
                                ) : (
                                    "—"
                                )
                            }
                        />
                        <InfoTile
                            icon={<KeyRound size={18} />}
                            label="Portal Login"
                            value={customer.user ? <Badge tone="green">{customer.user.username}</Badge> : <span className="text-slate-500">No login</span>}
                        />
                        <InfoTile icon={<UserCircle2 size={18} />} label="Seller" value={customer.seller?.name ?? "—"} />
                        <InfoTile icon={<Hash size={18} />} label="Created" value={formatDate(customer.createdAt)} />
                    </div>
                </Card>

                {/* Products */}
                <div className="2xl:col-span-2 space-y-6 min-w-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Summary label="Total value" value={formatMoney(summary.total)} />
                        <Summary label="Hardware" value={String(summary.hardware)} />
                        <Summary label="Software" value={String(summary.software)} />
                        <Summary label="Unpaid / partial" value={String(summary.unpaid)} warn={summary.unpaid > 0} />
                    </div>

                    <Card padded={false}>
                        <div className="p-5 pb-0">
                            <CardHeader title="Products" subtitle="Hardware and subscriptions for this customer" icon={Package} />
                        </div>
                        <CustomerProductTable
                            rows={productsReq.data}
                            loading={productsReq.loading}
                            error={productsReq.error}
                            onRetry={productsReq.reload}
                            showCustomer={false}
                            onEdit={
                                isAdmin
                                    ? (r) => {
                                          setEditingCp(r);
                                          setCpFormOpen(true);
                                      }
                                    : undefined
                            }
                            onDelete={isAdmin ? setDeletingCp : undefined}
                            emptyTitle="No products yet"
                            emptyText="Assign hardware or a software subscription to this customer."
                            emptyAction={
                                <Button size="sm" icon={Plus} onClick={openAssign}>
                                    Assign product
                                </Button>
                            }
                        />
                    </Card>
                </div>
            </div>

            <CustomerFormModal
                open={editOpen}
                customer={customer}
                onClose={() => setEditOpen(false)}
                onSaved={() => {
                    setEditOpen(false);
                    customerReq.reload();
                }}
            />

            <CustomerProductFormModal
                open={cpFormOpen}
                item={editingCp}
                fixedCustomer={customer}
                onClose={() => setCpFormOpen(false)}
                onSaved={() => {
                    setCpFormOpen(false);
                    productsReq.reload();
                }}
            />

            <ConfirmDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                title="Delete customer?"
                message={
                    <>
                        <b className="text-white">{customer.name}</b> will be removed
                        {customer.user ? " and their portal login disabled" : ""}. This can't be undone.
                    </>
                }
            />

            <ConfirmDialog
                open={!!deletingCp}
                onClose={() => setDeletingCp(null)}
                onConfirm={handleDeleteCp}
                title="Remove product?"
                confirmLabel="Remove"
                message={
                    <>
                        <b className="text-white">{deletingCp?.product.name}</b> will be removed from this customer.
                    </>
                }
            />
        </>
    );
}

function Summary({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
    return (
        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`text-lg font-bold mt-1 truncate ${warn ? "text-amber-400" : "text-white"}`}>{value}</p>
        </div>
    );
}